import {createApp} from 'vue'
import App from './App.vue'
import {buildRouter} from './router'
import './styles.css'
import './next/styles.css'
import './next/leadiq-skin.css'
import {getUiMode} from './composables/useUiMode'
import {selectRouteTable} from './routeTable'
import {loadCurrentUser, type AccessMode} from './services/user'
import {checkAndRefreshToken, isTokenExpiringSoon, refreshTokenWithLock} from './services/tokenRefresh'
import {getAccessToken, getAuthMode, getImpersonationState, logout} from './services/auth'
import {captureBackPath} from './services/navigation'
import urql, {cacheExchange, fetchExchange, Client, Operation, CombinedError} from "@urql/vue"
import {authExchange} from '@urql/exchange-auth'
import type {AuthConfig, AuthUtilities} from "@urql/exchange-auth";
import {GRAPHQL_API_URL} from "./env"

// The primary app's hand-off (?back=/app/lists) for the dialer shell's "Main
// Menu": captured and stripped per tab before anything else runs, whatever the
// access mode, so a login round-trip cannot lose it.
captureBackPath()

// Support "next" vs legacy UI (before any component renders). Provisional —
// re-asserted below once the access mode resolves.
document.documentElement.setAttribute('data-ui', getUiMode())

// Proactively check and refresh token on startup. Kicked before the identity
// await below — it shares a Web Lock with apiFetch's own 401 refresh-retry,
// so the ordering is a latency optimization, never a race.
if (getAuthMode() === 'external') {
  checkAndRefreshToken().catch(err => {
    console.error('Initial token refresh check failed:', err)
  })
}

// Resolve the access mode before the router exists — the route table IS the
// dialer-only allowlist. No token (login/callback boots) → 'full' immediately;
// /auth/me failure → null → 'full' (server-side 403s still enforce access).
let accessMode: AccessMode = 'full'
if (getAccessToken()) {
  const me = await loadCurrentUser()
  if (me?.access_mode === 'dialer_only') accessMode = 'dialer_only'
}
if (selectRouteTable(accessMode) === 'dialer') {
  // Dialer-only trumps ui_mode — no reload, just restyle (all of
  // next/styles.css is scoped to [data-ui="next"]).
  document.documentElement.setAttribute('data-ui', 'next')
  // …and re-point the next tokens at LeadIQ values before anything mounts, so
  // the first paint and the wait for the async shell chunk are already white
  // (next/leadiq-skin.css).
  document.documentElement.setAttribute('data-skin', 'leadiq')
}

const app = createApp(App).use(buildRouter(accessMode))

if (getAuthMode() === 'external') {

  const auth = authExchange(async (utils: AuthUtilities) => {
    let token = getAccessToken()
    let impersonationToken = getImpersonationState().token
    
    return {
      addAuthToOperation (operation: Operation): Operation {
        if (!token) return operation
        else {
          return utils.appendHeaders(operation, {
            Authorization: `Bearer ${token}`,
            'X-Impersonate': impersonationToken ?? undefined
          })
        }
      },
      didAuthError(error: CombinedError, operation_: Operation): boolean {
        return error.graphQLErrors.some(e => e.extensions?.code === 'FORBIDDEN') ||
          error.response?.status == 401
      },
      async refreshAuth (): Promise<void> {
        const refreshed = await refreshTokenWithLock()
        if (!refreshed) return logout()
      },
      willAuthError (_operation: Operation): boolean {
        return isTokenExpiringSoon(token, 300)
      }
    }
  })

  const client = new Client({
    url: GRAPHQL_API_URL,
    exchanges: [cacheExchange, auth, fetchExchange],
    preferGetMethod: false
  })

  app.use(urql, client)
}

app.mount('#app')
